import { useCallback, useEffect, useRef } from "react";

import { useMutation } from "@tanstack/react-query";

import { ONBOARDING_AGENT_DISPLAY_NAME } from "../../onboardingAgentConstants";
import type { OnboardingAgentResponse } from "../../onboardingAgent.types";

import { onboardingAgentApiService } from "../onboardingAgentApiService";

const ABORT_ERROR_NAME = "AbortError";
const ABORTED_OPERATION_MESSAGE = "This operation was aborted";

const isAbortError = (error: unknown): boolean =>
    error instanceof Error && (error.name === ABORT_ERROR_NAME || error.message === ABORTED_OPERATION_MESSAGE);

const toError = (error: unknown): Error =>
    error instanceof Error ? error : new Error(`${ONBOARDING_AGENT_DISPLAY_NAME} request failed`);

export type OnboardingAgentMessageVariables = {
    readonly requestId: string;
    readonly message: string;
    readonly threadId: string | null;
    readonly isSilent?: boolean;
};

export type UseOnboardingAgentMessageMutationParams = {
    readonly onSuccess: (response: OnboardingAgentResponse, variables: OnboardingAgentMessageVariables) => void;
    readonly onError: (error: Error, variables: OnboardingAgentMessageVariables) => void;
    readonly onSettled: (variables: OnboardingAgentMessageVariables) => void;
};

export type UseOnboardingAgentMessageMutationResult = {
    readonly send: (variables: OnboardingAgentMessageVariables) => void;
    readonly cancel: () => void;
    readonly isPending: boolean;
};

export const useOnboardingAgentMessageMutation = ({
    onSuccess,
    onError,
    onSettled,
}: UseOnboardingAgentMessageMutationParams): UseOnboardingAgentMessageMutationResult => {
    const abortControllerRef = useRef<AbortController | null>(null);

    const mutation = useMutation({
        mutationFn: async (variables: OnboardingAgentMessageVariables): Promise<OnboardingAgentResponse> => {
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                return await onboardingAgentApiService.sendMessage(
                    {
                        url: window.location.href,
                        message: variables.message,
                        threadId: variables.threadId,
                    },
                    { cancellation: controller.signal },
                );
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
            }
        },
        onSuccess,
        onError: (error, variables) => {
            if (isAbortError(error)) {
                return;
            }
            onError(toError(error), variables);
        },
        onSettled: (_data, _error, variables) => {
            onSettled(variables);
        },
    });

    const cancel = useCallback((): void => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, []);

    useEffect(
        () => () => {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
        },
        [],
    );

    return {
        send: mutation.mutate,
        cancel,
        isPending: mutation.isPending,
    };
};
